import { Deck, DeckProps } from "@deck.gl/core";
import * as L from "leaflet";

import { createDeckInstance, updateDeckView } from "./deck-utils";

export class LeafletLayer extends L.Layer {
  _container: HTMLElement | undefined = undefined;

  _deck: Deck | undefined = undefined;

  _animate: boolean | undefined = undefined;

  props: DeckProps;

  _zoomAnimated: boolean | undefined = undefined;

  constructor(props: DeckProps) {
    super();

    this.props = props;
  }

  override onAdd(): this {
    this._container = L.DomUtil.create("div");
    this._container.className = "leaflet-layer";
    if (this._zoomAnimated) {
      L.DomUtil.addClass(this._container, "leaflet-zoom-animated");
    }

    this.getPane()?.appendChild(this._container);
    this._deck = createDeckInstance(this._map, this._container, this._deck, this.props);
    this._update();

    return this;
  }

  override onRemove(_map: L.Map): this {
    if (!this._container || !this._deck) return this;
    L.DomUtil.remove(this._container);
    this._container = undefined;

    this._deck.finalize();
    this._deck = undefined;

    return this;
  }

  override getEvents(): { [name: string]: L.LeafletEventHandlerFn } {
    const events = {
      viewreset: this._reset,
      movestart: this._onMoveStart,
      moveend: this._onMoveEnd,
      zoomstart: this._onZoomStart,
      zoom: this._onZoom,
      zoomend: this._onZoomEnd,
    };
    if (this._zoomAnimated) {
      Object.assign(events, { zoomanim: this._onAnimZoom });
    }
    return events;
  }

  setProps(props: DeckProps): void {
    Object.assign(this.props, props);

    if (this._deck) {
      this._deck.setProps(props);
    }
  }

  pickObject(params: any): any {
    return this._deck && this._deck.pickObject(params);
  }

  pickMultipleObjects(params: any): any {
    return this._deck && this._deck.pickMultipleObjects(params);
  }

  pickObjects(params: any): any {
    return this._deck && this._deck.pickObjects(params);
  }

  _update(): void {
    if ((this._map as any)._animatingZoom || !this._container || !this._deck) {
      return;
    }

    const size = this._map.getSize();
    this._container.style.width = `${size.x}px`;
    this._container.style.height = `${size.y}px`;

    // invert map position
    const offset = (this._map as any)._getMapPanePos().multiplyBy(-1);
    L.DomUtil.setPosition(this._container, offset);

    updateDeckView(this._deck, this._map);
  }

  _pauseAnimation(): void {
    if (this._deck && this._deck.props._animate) {
      this._animate = this._deck.props._animate;
      this._deck.setProps({ _animate: false });
    }
  }

  _unpauseAnimation(): void {
    if (this._deck && this._animate) {
      this._deck.setProps({ _animate: this._animate });
      this._animate = undefined;
    }
  }

  _reset(): void {
    this._updateTransform(this._map.getCenter(), this._map.getZoom());
    this._update();
  }

  _onMoveStart(): void {
    this._pauseAnimation();
  }

  _onMoveEnd(): void {
    this._update();
    this._unpauseAnimation();
  }

  _onZoomStart(): void {
    this._pauseAnimation();
  }

  _onAnimZoom(event: L.ZoomAnimEvent): void {
    this._updateTransform(event.center, event.zoom);
  }

  _onZoom(): void {
    this._updateTransform(this._map.getCenter(), this._map.getZoom());
  }

  _onZoomEnd(): void {
    this._unpauseAnimation();
  }

  _updateTransform(center: L.LatLng, zoom: number) {
    if (!this._container) return;
    const scale = this._map.getZoomScale(zoom, this._map.getZoom());
    const position = L.DomUtil.getPosition(this._container);
    const viewHalf = this._map.getSize().multiplyBy(0.5);
    const currentCenterPoint = this._map.project(this._map.getCenter(), zoom);
    const destCenterPoint = this._map.project(center, zoom);
    const centerOffset = destCenterPoint.subtract(currentCenterPoint);
    const topLeftOffset = viewHalf
      .multiplyBy(-scale)
      .add(position)
      .add(viewHalf)
      .subtract(centerOffset);

    if (L.Browser.any3d) {
      L.DomUtil.setTransform(this._container, topLeftOffset, scale);
    } else {
      L.DomUtil.setPosition(this._container, topLeftOffset);
    }
  }
}
